import { Controller } from "@hotwired/stimulus";
import { heroicon } from "../../../lib/heroicon";
import videoCameraSvg from "heroicons/20/solid/video-camera.svg?raw";
import photoSvg from "heroicons/20/solid/photo.svg?raw";

const ICON_SVGS = { video: videoCameraSvg, photo: photoSvg };

const byRole = (el, role) => el.querySelector(`[data-role="${role}"]`);

/**
 * Renders a single media-track segment.
 *
 * Manages 4 visual states: ready, placeholder, generating, failed.
 * The parent tracks controller sets data-*-value attributes and this
 * controller swaps its internal DOM and updates it reactively.
 */
export default class extends Controller {
  static targets = ["readyTpl", "placeholderTpl", "generatingTpl", "failedTpl"];

  static values = {
    state: String,       // "ready" | "placeholder" | "generating" | "failed"
    title: String,
    background: String,
    selected: Boolean,
    speed: Number,
    repeat: Number,
    slot: String,
    icon: String,
    thumbnails: Object,  // { kind, background?, frames?: [{ bg }] }
    segIndex: Number,
  };

  // ---- State swap ----

  stateValueChanged(v) {
    const cap = v.charAt(0).toUpperCase() + v.slice(1);
    if (!this[`has${cap}TplTarget`]) return;
    const el = this[`${v}TplTarget`].content.cloneNode(true).firstElementChild;
    this._body = el;
    this.element.replaceChildren(...this.element.querySelectorAll("template"), el);
    this._applyState();
  }

  // ---- Reactive value callbacks ----

  titleValueChanged()       { if (this._body) this._applyTitle(); }
  backgroundValueChanged()  { if (this._body) this._applyBackground(); }
  selectedValueChanged()    { if (this._isReady) this._applySelected(); }
  speedValueChanged()       { if (this._isReady) this._applySpeed(); }
  repeatValueChanged()      { if (this._isReady) this._applyRepeat(); }
  slotValueChanged()        { if (this._isReady) this._applySlot(); }
  iconValueChanged()        { if (this._isReady) this._applyIcon(); }
  thumbnailsValueChanged()  { if (this._isReady) this._applyThumbnails(); }

  segIndexValueChanged(v) {
    this.element.dataset.segIndex = v;
    if (this._isReady) {
      const attr = "data-composition-editor--tracks-seg-index-param";
      this._body.querySelectorAll(`[${attr}]`).forEach(h => h.setAttribute(attr, v));
    }
  }

  // ---- Private ----

  get _isReady() { return this._body && this.stateValue === "ready"; }

  _applyState() {
    this._applyTitle();
    this._applyBackground();
    if (this._isReady) {
      this._applySelected();
      this._applySpeed();
      this._applyRepeat();
      this._applySlot();
      this._applyIcon();
      this._applyThumbnails();
      this.segIndexValueChanged(this.segIndexValue);
    }
  }

  _applyTitle() {
    this._body.title = this.titleValue || "";
    const el = byRole(this._body, "label-text");
    if (el) el.textContent = this.titleValue;
  }

  _applyBackground() {
    const el = byRole(this._body, "source-image");
    if (el && this.backgroundValue) {
      el.style.backgroundImage = `url('${this.backgroundValue}')`;
    }
  }

  _applySelected() {
    const v = this.selectedValue;
    this._body.toggleAttribute("data-selected", v);
    byRole(this._body, "handle-l")?.toggleAttribute("data-selected", v);
    byRole(this._body, "handle-r")?.toggleAttribute("data-selected", v);
  }

  _applyRepeat() {
    const badge = byRole(this._body, "repeat-badge");
    if (!badge) return;
    const v = this.repeatValue;
    badge.toggleAttribute("data-has-repeat", !!v);
    badge.textContent = v ? `×${v}` : "";
  }

  // Lié à un média du sujet : le nom (le titre) dit déjà le slot, le clip prend la couleur.
  _applySlot() {
    this._body.toggleAttribute("data-slot", !!this.slotValue);
  }

  _applySpeed() {
    const badge = byRole(this._body, "speed-badge");
    if (!badge) return;
    const v = this.speedValue;
    badge.toggleAttribute("data-has-speed", !!v);
    badge.textContent = v ? `${v}x` : "";
  }

  _applyIcon() {
    const container = byRole(this._body, "type-icon");
    if (!container || !this.iconValue) return;
    container.replaceChildren(heroicon(ICON_SVGS[this.iconValue], "w-2.5 h-2.5"));
  }

  _applyThumbnails() {
    const strip = byRole(this._body, "strip");
    const v = this.thumbnailsValue;
    if (!strip || !v?.kind) return;

    if (v.kind === "color") {
      strip.innerHTML = `<span class="flex-1 rounded-lg" style="background:${v.background}"></span>`;
    } else {
      strip.innerHTML = (v.frames || [])
        .map(f => `<span class="flex-1 border-r border-white/20 bg-cover bg-center last:border-r-0" style="background-image:${f.bg}"></span>`)
        .join("");
    }
  }
}
